'use client';
import { useTranslations } from 'next-intl';
import { useServiceInsights } from 'hooks/use-service-insights';
import { CatTooltip } from '@blocks-react/components/Tooltip';
import { CatIconTrendingUp } from '@blocks-react/components/Icons/TrendingUp';
import { CatTextPassage } from 'components/blocks-components';
import { BlocksIconTrendingUp } from 'blocks-nextjs/blocks-web-components/components/icon/icons/trending-up';
import {
  Conditional,
  ServiceInsightButton,
  useBreakpoint
} from 'components/pcc-components';
import { LOCATION_HASH_KEY } from '../constants';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { Equipment, ServiceInsightDetailProps } from 'services/equipment/types';
import cx from 'classnames';
import styles from './ServiceInsightIcon.module.scss';

interface ServiceInsightProps {
  serviceInsightsDetails: ServiceInsightDetailProps;
  equipmentDetails: Equipment;
  highlightSelectedEquipment?: boolean;
  isCspUser: boolean;
  isDisplayLink?: boolean;
  isMultiple?: boolean;
  isLargeIcon?: boolean;
  classname?: string;
}

export const ServiceInsightIcon = (props: Readonly<ServiceInsightProps>) => {
  const t = useTranslations();
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.XL);
  const {
    serviceInsightsDetails,
    equipmentDetails,
    highlightSelectedEquipment = false,
    isCspUser = false,
    isDisplayLink = false,
    isMultiple = false,
    isLargeIcon = false,
    classname
  } = props;

  const { serviceInsightEnabled } = useServiceInsights(
    serviceInsightsDetails,
    equipmentDetails,
    isCspUser
  );

  const serviceInsightRedirection = () => {
    if (equipmentDetails?.seoUrl) {
      sessionStorage.setItem('focusServiceInsight', 'true');
      window.location.href = `${equipmentDetails?.seoUrl}${LOCATION_HASH_KEY.SERVICE_INSIGHT}`;
    }
  };

  const handlePreventSelection = (
    e: React.MouseEvent<BlocksIconTrendingUp, MouseEvent>
  ) => {
    if (!isDesktop) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Conditional test={serviceInsightEnabled}>
      <Conditional
        test={!isDisplayLink}
        fallback={
          <ServiceInsightButton
            onClick={serviceInsightRedirection}
            isLargeIcon={isLargeIcon}
            classname={classname}
          >
            {t('SERVICE_INSIGHTS_AVBL')}
          </ServiceInsightButton>
        }
      >
        <CatTooltip position="auto" isInteractive={!isDesktop}>
          <CatIconTrendingUp
            slot="trigger"
            className={cx(styles['service_recommendation_icon'], {
              [styles['service_recommendation_icon_spacing_large']]:
                highlightSelectedEquipment && !isMultiple,
              [styles['service_recommendation_icon_spacing_small']]:
                highlightSelectedEquipment && isMultiple
            })}
            size="sm"
            data-testid="service-recommendation-icon"
            tabIndex={0}
            aria-label={t('SERVICE_INSIGHTS_AVAILABLE')}
            onClick={handlePreventSelection}
          />
          <CatTextPassage size="xs">
            {t('SERVICE_INSIGHTS_AVAILABLE')}
          </CatTextPassage>
        </CatTooltip>
      </Conditional>
    </Conditional>
  );
};
